import React from 'react';

import { Flex } from 'antd-v5';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import ResizeObserver from 'resize-observer-polyfill';
import { Button, Text } from 'scouting-ui/lib';

import { isMobileSel } from '../../duck';
import Card from '../Card';
import ConditionalRender from '../ConditionalRender';
import Dropdown from '../Dropdown';
import Fab from '../Fab';
import styles from './TableFilterCard.less';
import TableFilterCollapse from './TableFilterCollapse';
import TableFilterPanel from './TableFilterPanel';
import TableFilterPanelGroup from './TableFilterPanelGroup';

const dropdownTrigger = ['click'];
// need to get ref to overlay on per-instance basis
// Dropdown does not work with `ref` so we need to target by unique class name
const overlayClassName = 'TabFilterCard_Overlay';
let instanceCounter = 0;

/**
 *
 * This component's parent will be used as reference for width/left offset.
 * So usually you would want to place this inside your table/list header.
 *
 */
class TableFilterCard extends React.Component {
  state = {
    open: this.props.defaultOpen || false,
  };

  componentDidMount = () => {
    // TODO: refactor usage of findDOMNode
    // eslint-disable-next-line react/no-find-dom-node
    this.parent = ReactDOM.findDOMNode(this).parentNode;
    this.overlay = document.querySelector(`.${this.instanceOverlayClassName}`);
    this.resizeObserver = new ResizeObserver(
      throttle(this.asyncUpdateOverlay, 30),
    );
    this.resizeObserver.observe(this.parent);
  };

  componentDidUpdate = () => {
    if (!this.overlay && this.isOpen()) {
      setTimeout(() => {
        this.overlay = document.querySelector(
          `.${this.instanceOverlayClassName}`,
        );
        this.updateOverlay();
      });
    }
    this.updateOverlay();
  };

  componentWillUnmount = () => {
    if (this.resizeObserver && this.parent) {
      this.resizeObserver.unobserve(this.parent);
    }
    this.handleBodyScroll(false);
  };

  instanceOverlayClassName = `${overlayClassName}${instanceCounter++}`;
  overlay = null;
  resizeObserver = null;
  parent = null;

  asyncUpdateOverlay = () => setTimeout(this.updateOverlay);

  updateOverlay = () => {
    if (this.overlay && this.parent && this.isOpen()) {
      const { isMobile } = this.props;
      const style = isMobile ? '' : this.getDesktopOverlayStyle(this.overlay);
      this.overlay.setAttribute('style', style);
    }
  };

  getDesktopOverlayStyle = overlay => {
    const { x, width } = this.parent.getBoundingClientRect();

    return `
      top: ${overlay.style.top};
      margin-left: ${x}px !important;
      min-width: ${width}px !important;
    `;
  };

  handleBodyScroll = isOpen => {
    const { isMobile } = this.props;
    if (isOpen && isMobile) {
      setTimeout(() => {
        const content = document.querySelector(
          `.${this.instanceOverlayClassName} .${styles.content}`,
        );
        disableBodyScroll(content);
      });
    } else if (!isOpen) {
      clearAllBodyScrollLocks();
    }
  };

  handleOpenChange = () => {
    const { onOpenChange } = this.props;
    if (onOpenChange) {
      onOpenChange(!this.isOpen());
    }
    if (!this.isControlled()) {
      this.setState(({ open }) => ({ open: !open }));
    }
    this.asyncUpdateOverlay();
    this.handleBodyScroll(!this.isOpen());
  };

  handleClose = () => {
    const { onOpenChange } = this.props;
    if (onOpenChange) {
      onOpenChange(false);
    }
    if (!this.isControlled()) {
      this.setState({ open: false });
    }

    this.handleBodyScroll(false);
  };

  handleSubmit = () => {
    const { onSubmit } = this.props;
    if (onSubmit) {
      onSubmit();
    }
    this.handleClose();
  };

  isOpen = () => {
    const { open } = this.props;
    const { open: stateOpen } = this.state;
    return this.isControlled() ? open : stateOpen;
  };

  isControlled = () => typeof this.props.open === 'boolean';

  renderContent = () => {
    const {
      content,
      className,
      isMobile,
      submitId,
      extraActions,
      topLeftAction,
    } = this.props;
    const classNames = cn(className, styles.card);

    return (
      // extra <div> with those attrs because Dropdown applies them to its child and Card would complain
      // eslint-disable-next-line react/no-unknown-property
      <div selectable="false" focusable="false">
        <Card shadow className={classNames}>
          <div className={styles.container}>
            <div className={styles.header}>
              {topLeftAction ? (
                topLeftAction
              ) : (
                <Text color="color-white">
                  <FormattedMessage id="shared.TableFilterCard.title" />
                </Text>
              )}
              <Button variant="text" color="white" onClick={this.handleClose}>
                <FormattedMessage id="shared.cancel" />
              </Button>
            </div>
            <div className={styles.content}>{content}</div>
            {isMobile ? (
              <Fab
                noBorder
                fitText
                color="scouting-blue"
                id={submitId}
                onClick={this.handleSubmit}
              >
                <FormattedMessage id="shared.TableFilterCard.submit" />
              </Fab>
            ) : (
              <>
                <Flex
                  className={styles.collapseActions}
                  gap={0}
                  align="center"
                  justify="flex-end"
                >
                  <ConditionalRender value={!!extraActions}>
                    {extraActions}
                  </ConditionalRender>
                  <Button type="text" id={submitId} onClick={this.handleSubmit}>
                    <FormattedMessage id="shared.TableFilterCard.submit" />
                  </Button>
                </Flex>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  };

  render() {
    const { children } = this.props;

    return (
      <Dropdown
        trigger={dropdownTrigger}
        placement="bottomLeft"
        overlay={this.renderContent()}
        overlayClassName={cn(styles.overlay, this.instanceOverlayClassName)}
        visible={this.isOpen()}
        onVisibleChange={this.handleOpenChange}
      >
        {children}
      </Dropdown>
    );
  }
}

TableFilterCard.propTypes = {
  // provided by parent
  // child must be an element that has an 'onClick' prop (preferably a button) :P
  children: PropTypes.element.isRequired,
  extraActions: PropTypes.element,
  /** Used in mobile version */
  topLeftAction: PropTypes.element,
  content: PropTypes.node.isRequired,
  className: PropTypes.string,
  submitId: PropTypes.string,
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onSubmit: PropTypes.func,
  // provided by component itself
  isMobile: PropTypes.bool.isRequired,
};

TableFilterCard.Collapse = TableFilterCollapse;
TableFilterCard.Panel = TableFilterPanel;
TableFilterCard.PanelGroup = TableFilterPanelGroup;

const mapState = state => ({
  isMobile: isMobileSel(state),
});

export default connect(mapState)(TableFilterCard);
